import React from 'react';

import GlassCard from 'shared/components/GlassCard';
import { cn } from 'shared/lib/utils';

interface StepCardProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  /**
   * Optional fully-custom header. When provided it replaces the default
   * title/subtitle block, letting callers render a richer hero
   * (eyebrow chip, gradient text, badges, etc.).
   */
  headerSlot?: React.ReactNode;
  /**
   * When true, renders soft decorative blobs behind the card to give the
   * step a warmer, more ambient feel. Off by default so other steps stay
   * visually quiet.
   */
  ambient?: boolean;
  /** Keep the card frame visible while its content scrolls internally. */
  scrollable?: boolean;
}

const StepCard: React.FC<StepCardProps> = ({
  title,
  subtitle,
  children,
  headerSlot,
  ambient = false,
  scrollable = false,
}) => {
  const header = headerSlot ?? (
    <header className="mb-5 sm:mb-7">
      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">
        {title}
      </h2>
      {subtitle && (
        <p className="mt-1.5 text-sm text-gray-600 leading-relaxed">
          {subtitle}
        </p>
      )}
    </header>
  );

  return (
    <div className={cn('relative', scrollable && 'max-h-full min-h-0')}>
      {ambient && (
        <>
          {/* Warm apricot halo, top-end */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute -top-24 end-[-6rem] h-[420px] w-[420px] rounded-full bg-[#FFD7B5] opacity-50 blur-3xl animate-float"
          />
          {/* Soft rose halo, bottom-start */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute bottom-[-8rem] start-[-6rem] h-[360px] w-[360px] rounded-full bg-[#FFE4E0] opacity-60 blur-3xl"
          />
        </>
      )}
      <GlassCard
        noEnter
        className={cn(
          'relative',
          scrollable ? 'max-h-full min-h-0' : 'p-5 sm:p-8',
        )}
        contentClassName={
          scrollable
            ? 'max-h-full overflow-y-auto overscroll-contain p-5 sm:p-8'
            : undefined
        }
      >
        {header}
        {children}
      </GlassCard>
    </div>
  );
};

export default StepCard;
